import { ProductComponent } from '~/components/ProductPage/WardrobeProductPage'
import { useEffect, useState, useMemo, useCallback, useRef } from 'react'
import { useRouter } from '~/hooks/useRouter'
import { colorHexCodes, ColorName } from '~/utils/colorDictionary'
import { WardrobeColumnConfiguration } from '~/types/wardrobeConfigurationTypes'
import { calculateWardrobeColumnLayout } from '~/utils/wardrobeColumnLayout'
import {
  WARDROBE_TEMPLATES,
  WardrobeTemplateId,
  getValidTemplates,
} from '~/config/wardrobeTemplates'
import type { WardrobeTemplate } from '~/types/wardrobeConfigurationTypes'
import { useConfiguratorConfigOptional } from '~/context/urlConfigContext'
import {
  encodeWardrobeColumnConfigs,
  decodeWardrobeColumnConfigs,
} from '~/utils/wardrobeColumnConfigUrl'
import { OpeningType } from '~/components/ThreeDModel/furnitureConfig'

export interface WardrobeConfigOptions {
  /** Available handle types for this wardrobe variant */
  handleOptions: OpeningType[]
  /** Default handle type */
  defaultHandle: OpeningType
  /** Available color names */
  colorOptions: string[]
  /** Default color name (e.g. 'White') */
  defaultColor: string
  /** Available templates (defaults to WARDROBE_TEMPLATES if not provided) */
  templates?: Record<string, WardrobeTemplate>
  /** Dimension ranges */
  widthRange?: [number, number]
  heightRange?: [number, number]
  depthRange?: [number, number]
  plintHeightRange?: [number, number]
  heightStep?: number
}

function getDefaultColorHex(name: string): string {
  const map: Record<string, string> = {
    White: colorHexCodes[ColorName.White],
    Beige: colorHexCodes[ColorName.Beige],
    'Light Grey': colorHexCodes[ColorName.LightGrey],
    Grey: colorHexCodes[ColorName.Grey],
    'Brown Belmont Oak': colorHexCodes[ColorName.BrownOak],
  }
  return map[name] ?? colorHexCodes[ColorName.White]
}

/**
 * Factory function to create a configurable wardrobe product configurator.
 * Allows different wardrobe collections to have different handles, colors, and templates.
 */
export function createWardrobeConfigurator(
  options: WardrobeConfigOptions
): () => ProductComponent[] {
  const {
    handleOptions,
    defaultHandle,
    colorOptions,
    defaultColor,
    templates = WARDROBE_TEMPLATES,
    widthRange = [40, 250],
    heightRange = [200, 280],
    depthRange = [35, 60],
    plintHeightRange = [2, 8],
    heightStep = 1,
  } = options

  const isNewCollection = defaultHandle === OpeningType.NewCollectionHandle

  return function WardrobeProductConfigurator(): ProductComponent[] {
    const urlCtx = useConfiguratorConfigOptional()
    const router = useRouter()

    const [width, setWidth] = useState(200)
    const [height, setHeight] = useState(260)
    const [depth, setDepth] = useState(50)
    const [plintHeight, setPlintHeight] = useState(2)
    const [selectedColor, setSelectedColor] = useState(() =>
      getDefaultColorHex(defaultColor)
    )

    const [isMirrored, setIsMirrored] = useState(false)

    const columnLayout = useMemo(() => {
      return calculateWardrobeColumnLayout(width, isMirrored, isNewCollection)
    }, [width, isMirrored])

    const [columnConfigurations, setColumnConfigurations] = useState<
      WardrobeColumnConfiguration[]
    >(() => {
      return columnLayout.columnWidths.map((colWidth, index) => {
        const defaultTemplateId = WardrobeTemplateId.FULL_HANGING_WITH_1_SHELF
        const template = templates[defaultTemplateId]
        return {
          zones: template?.zones || [],
          totalHeight: height - plintHeight,
          doorType: colWidth > 60 ? 'split' : 'single',
          doorOpeningSide: columnLayout.doorOpeningSides[index],
          templateId: defaultTemplateId,
          hasDoor: true,
        }
      })
    })

    const [urlInitialized, setUrlInitialized] = useState(false)
    const [guides, setGuides] = useState('standart')

    const [openingOption, setOpeningOption] = useState<OpeningType>(() => {
      if (!urlCtx?.config.openingType) return defaultHandle
      if (urlCtx.config.openingType === 'profile')
        return OpeningType.ProfileHandle
      if (urlCtx.config.openingType === 'profile-long')
        return OpeningType.ProfileHandleLong
      if (urlCtx.config.openingType === 'round') return OpeningType.RoundHandle
      if (urlCtx.config.openingType === 'new-collection-handle')
        return OpeningType.NewCollectionHandle
      if (urlCtx.config.openingType === 'push') return OpeningType.Push
      return defaultHandle
    })

    useEffect(() => {
      if (!handleOptions.includes(openingOption)) {
        setOpeningOption(defaultHandle)
      }
    }, [openingOption, handleOptions, defaultHandle])

    useEffect(() => {
      if (urlCtx?.config.isMirrored !== undefined) {
        setIsMirrored(urlCtx.config.isMirrored)
      }
    }, [urlCtx?.config.isMirrored])

    const hasHydratedRef = useRef(false)
    const prevMirroredRef = useRef(isMirrored)
    const prevDimensionsRef = useRef({
      width: 200,
      height: 260,
      plintHeight: 2,
      columnCount: columnLayout.columnCount,
    })

    useEffect(() => {
      hasHydratedRef.current = true
    }, [])

    useEffect(() => {
      if (!urlCtx || !hasHydratedRef.current) return
      if (urlCtx.config.width !== undefined && urlCtx.config.width !== width) {
        setWidth(urlCtx.config.width)
      }
      if (
        urlCtx.config.height !== undefined &&
        urlCtx.config.height !== height
      ) {
        setHeight(urlCtx.config.height)
      }
      if (urlCtx.config.depth !== undefined && urlCtx.config.depth !== depth) {
        setDepth(urlCtx.config.depth)
      }
      if (
        urlCtx.config.plintHeight !== undefined &&
        urlCtx.config.plintHeight !== plintHeight
      ) {
        setPlintHeight(urlCtx.config.plintHeight)
      }
    }, [
      urlCtx?.config.width,
      urlCtx?.config.height,
      urlCtx?.config.depth,
      urlCtx?.config.plintHeight,
    ])

    const updateColumnConfigurations = useCallback(
      (configs: WardrobeColumnConfiguration[]) => {
        setColumnConfigurations(configs)
        if (urlCtx) {
          const encoded = encodeWardrobeColumnConfigs(configs)
          urlCtx.setConfig({ ...urlCtx.config, wardrobeCfg: encoded })
        }
      },
      [urlCtx]
    )

    const toggleMirror = useCallback(() => {
      const newMirrorState = !isMirrored
      setIsMirrored(newMirrorState)
      if (urlCtx) {
        urlCtx.setConfig({ ...urlCtx.config, isMirrored: newMirrorState })
      }
    }, [isMirrored, urlCtx, width, columnConfigurations])

    useEffect(() => {
      if (!urlInitialized || !hasHydratedRef.current) return
      if (prevMirroredRef.current === isMirrored) return
      prevMirroredRef.current = isMirrored
      const newLayout = calculateWardrobeColumnLayout(
        width,
        isMirrored,
        isNewCollection
      )
      setColumnConfigurations((prevConfigs) => {
        const reversed = [...prevConfigs].reverse()
        const reversedWithUpdatedDoors = reversed.map((config, index) => {
          const newColumnWidth = newLayout.columnWidths[index]
          const newDoorOpeningSide = newLayout.doorOpeningSides[index]
          if (config.doorType === 'single' && newColumnWidth <= 60) {
            return { ...config, doorOpeningSide: newDoorOpeningSide }
          }
          return config
        })
        if (urlCtx) {
          const encoded = encodeWardrobeColumnConfigs(reversedWithUpdatedDoors)
          urlCtx.setConfig({ ...urlCtx.config, wardrobeCfg: encoded })
        }
        return reversedWithUpdatedDoors
      })
    }, [isMirrored, urlInitialized, urlCtx, width])

    useEffect(() => {
      if (!urlInitialized && router.isReady && urlCtx) {
        const wardrobeCfgStr = urlCtx.config.wardrobeCfg
        if (wardrobeCfgStr && typeof wardrobeCfgStr === 'string') {
          const decodedConfigs = decodeWardrobeColumnConfigs(wardrobeCfgStr)
          if (decodedConfigs.length > 0) {
            const urlWidth = urlCtx.config.width
            const urlMirrored = urlCtx.config.isMirrored ?? false
            const urlLayout = calculateWardrobeColumnLayout(
              urlWidth,
              urlMirrored,
              isNewCollection
            )
            const newConfigs = urlLayout.columnWidths.map((colWidth, index) => {
              const decoded = decodedConfigs[index] || {
                templateId: WardrobeTemplateId.FULL_HANGING_WITH_1_SHELF,
                hasDoor: true,
              }
              const templateId = decoded.templateId
              const template = templates[templateId]
              return {
                zones: template?.zones || [],
                totalHeight: height - plintHeight,
                doorType: (colWidth > 60 ? 'split' : 'single') as
                  | 'split'
                  | 'single',
                doorOpeningSide: urlLayout.doorOpeningSides[index],
                templateId,
                hasDoor: decoded.hasDoor,
              }
            })
            setColumnConfigurations(newConfigs)
            prevMirroredRef.current = urlMirrored
          }
        }
        setUrlInitialized(true)
      }
    }, [router.isReady, urlCtx, urlInitialized, height, plintHeight, templates])

    useEffect(() => {
      if (!urlInitialized || !hasHydratedRef.current) return
      const newLayout = calculateWardrobeColumnLayout(
        width,
        isMirrored,
        isNewCollection
      )
      const prev = prevDimensionsRef.current
      const columnCountChanged = prev.columnCount !== newLayout.columnCount
      const dimensionsChanged =
        prev.height !== height || prev.plintHeight !== plintHeight
      // Width must be included: same column count but different width can change
      // doorType and doorOpeningSide (e.g. 150cm→110cm both have 2 columns).
      const widthChanged = prev.width !== width
      if (!columnCountChanged && !dimensionsChanged && !widthChanged) return
      prevDimensionsRef.current = {
        width,
        height,
        plintHeight,
        columnCount: newLayout.columnCount,
      }
      if (columnCountChanged) {
        setColumnConfigurations((prevConfigs) =>
          newLayout.columnWidths.map((colWidth, index) => {
            const existingConfig = prevConfigs[index]
            const existingHasDoor = existingConfig?.hasDoor ?? true
            const colHeight = height - plintHeight
            const validTemplateIds = new Set(
              getValidTemplates(colWidth, colHeight, isNewCollection).map(
                (t) => t.id
              )
            )
            const existingTemplate = existingConfig?.templateId
            const isValid =
              existingTemplate && validTemplateIds.has(existingTemplate)
            const templateId = isValid
              ? existingTemplate
              : WardrobeTemplateId.FULL_HANGING_WITH_1_SHELF
            const template = templates[templateId]
            return {
              zones: template?.zones || [],
              totalHeight: colHeight,
              doorType: colWidth > 60 ? 'split' : 'single',
              doorOpeningSide: newLayout.doorOpeningSides[index],
              templateId,
              hasDoor: existingHasDoor,
            }
          })
        )
      } else {
        setColumnConfigurations((prevConfigs) =>
          prevConfigs.map((config, index) => {
            const colWidth = newLayout.columnWidths[index]
            const colHeight = height - plintHeight
            const validTemplateIds = new Set(
              getValidTemplates(colWidth, colHeight, isNewCollection).map(
                (t) => t.id
              )
            )
            const isTemplateValid = validTemplateIds.has(
              config.templateId || ''
            )
            const fallbackTemplateId = isTemplateValid
              ? config.templateId
              : WardrobeTemplateId.FULL_HANGING_WITH_1_SHELF
            const fallbackTemplate = templates[fallbackTemplateId || '']

            return {
              ...config,
              totalHeight: colHeight,
              doorType: colWidth > 60 ? 'split' : 'single',
              doorOpeningSide: newLayout.doorOpeningSides[index],
              templateId: fallbackTemplateId,
              zones: isTemplateValid
                ? config.zones
                : fallbackTemplate?.zones || config.zones,
            }
          })
        )
      }
    }, [
      width,
      height,
      plintHeight,
      urlInitialized,
      columnLayout.columnCount,
      isMirrored,
      templates,
    ])

    const handleSetOpeningOption = useCallback(
      (newOption: OpeningType) => {
        if (!handleOptions.includes(newOption)) return
        setOpeningOption(newOption)
        if (urlCtx) {
          let urlValue:
            | 'push'
            | 'round'
            | 'profile'
            | 'profile-long'
            | 'new-collection-handle'
          if (newOption === OpeningType.Push) urlValue = 'push'
          else if (newOption === OpeningType.ProfileHandle) urlValue = 'profile'
          else if (newOption === OpeningType.ProfileHandleLong)
            urlValue = 'profile-long'
          else if (newOption === OpeningType.NewCollectionHandle)
            urlValue = 'new-collection-handle'
          else urlValue = 'round'
          urlCtx.setConfig({ ...urlCtx.config, openingType: urlValue })
        }
      },
      [handleOptions, urlCtx]
    )

    return [
      {
        type: 'dimensions',
        widthRange,
        heightRange,
        depthRange,
        plintHeightRange,
        heightStep,
        width,
        setWidth,
        height,
        setHeight,
        plintHeight,
        setPlintHeight,
        depth,
        setDepth,
      },
      {
        type: 'colors',
        colors: colorOptions,
        selectedColor,
        setSelectedColor,
      },
      {
        type: 'wardrobeColumns',
        selectedColumns: columnLayout.columnCount,
        columnConfigurations,
        setColumnConfigurations: updateColumnConfigurations,
        columnWidths: columnLayout.columnWidths,
        columnWidth: columnLayout.columnWidths[0],
        columnHeight: height - plintHeight,
        columnDepth: depth,
        onMirrorToggle: toggleMirror,
        isMirrored,
        doorOpeningSides: columnLayout.doorOpeningSides,
        isNewCollection,
      } as unknown as ProductComponent,
      {
        type: 'furniture',
        openingOption,
        selectedOpeningMethod: openingOption,
        hinges: '',
        setOpeningOption: handleSetOpeningOption,
        guides,
        setGuides,
        is3DEnabled: true,
      },
      {
        // Price is provided live by the pricing-quote (see QuoteProvider); this
        // static value is unused for wardrobe and kept only to satisfy the type.
        type: 'price',
        price: 0,
      },
    ]
  }
}

export function getFullPalette(): string[] {
  return [
    ColorName.White,
    ColorName.Beige,
    ColorName.LightGrey,
    ColorName.Grey,
    ColorName.DarkGrey,
    ColorName.GreyCubanit,
    ColorName.GreyStone,
    ColorName.GreenSalvia,
    ColorName.BeigeSand,
    ColorName.BeigeCashmere,
    ColorName.BeigeAlmond,
    ColorName.RoseAntique,
    ColorName.NaturalAccacia,
    ColorName.BrownOak,
  ]
}

export function getNewCollectionPalette(): string[] {
  return [
    ColorName.White, // #fcfbf5  L≈97%
    ColorName.Beige, // #f0e3ce  L≈87%
    ColorName.BeigeSand, // #e7d5c3  L≈83%
    ColorName.BeigeCashmere, // #d9c9be  L≈80%
    ColorName.LightGrey, // #c4c0ba  L≈75%
    ColorName.GreenSalvia, // #bdbda6  L≈70%
    ColorName.RoseAntique, // #c39d94  L≈67%
    ColorName.BeigeAlmond, // #baa397  L≈66%
    ColorName.DenimBlue, // #93a7b2  L≈64%
    ColorName.GreyStone, // #a69989  L≈59%
    ColorName.GreenFjord, // #909e9e  L≈59%
    ColorName.Grey, // #9b9593  L≈59%
    ColorName.CaramelNude, // #c9845d  L≈58%
    ColorName.GreyCubanit, // #8a8076  L≈50%
    ColorName.MonumentGrey, // #777c84  L≈49%
    ColorName.DarkGrey, // #4b4b4b  L≈29%
    ColorName.GarnetRed, // #61292e  L≈27%
    ColorName.BlackBrown, // #422f29  L≈21%

    // Textures
    ColorName.NaturalAccacia, // #d4b896  L≈71%
    ColorName.BrownOak, // #5a3a1a  L≈23%
  ]
}
